import React, { useState } from 'react';
import { Modal, Input, Button, message, Card } from 'antd';
import { useSnapshot } from 'valtio';
import { CustomTemplateStore } from '@/utils/store/customTemplate';
import { EXAMPLE_TEMPLATES_LIST } from '@/constants/exampleTemplates';

const { TextArea } = Input;

interface AddTemplateModalProps {
  visible: boolean;
  onClose: () => void;
  userId: string;
}

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({ visible, onClose, userId }) => {
  const [title, setTitle] = useState('');
  const [info, setInfo] = useState('');
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [isFileGenerating, setIsFileGenerating] = useState(false);
  const { addLoading } = useSnapshot(CustomTemplateStore);

  const currentTemplate = EXAMPLE_TEMPLATES_LIST[currentTemplateIndex];

  const handleSubmit = async () => {
    if (!title.trim()) {
      message.warning('请输入模板标题');
      return;
    }
    
    if (!info.trim()) {
      message.warning('请输入纪要大纲');
      return;
    }

    const success = await CustomTemplateStore.addTemplate({
      title: title.trim(),
      info: info.trim(),
      userId
    });

    if (success) {
      message.success('自定义模板添加成功');
      setTitle('');
      setInfo('');
      onClose();
    }
  };

  const handleCancel = () => {
    setTitle('');
    setInfo('');
    setCurrentTemplateIndex(0);
    setAiPrompt('');
    setIsAiGenerating(false);
    setIsFileGenerating(false);
    onClose();
  };

  const handlePrevTemplate = () => {
    setCurrentTemplateIndex((prev) => 
      prev === 0 ? EXAMPLE_TEMPLATES_LIST.length - 1 : prev - 1
    );
  };

  const handleNextTemplate = () => {
    setCurrentTemplateIndex((prev) => 
      prev === EXAMPLE_TEMPLATES_LIST.length - 1 ? 0 : prev + 1
    );
  };

  // AI 生成模板处理函数
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      message.warning('请输入要点内容');
      return;
    }

    setIsAiGenerating(true);
    try {
      // 模拟接口调用延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      // 第一阶段：直接返回用户输入的原文作为生成结果
      setInfo(aiPrompt);
      message.success('模板生成成功');
    } catch (error) {
      message.error('生成失败，请重试');
    } finally {
      setIsAiGenerating(false);
    }
  };

  // 文件学习模板处理函数
  const handleFileGenerate = async () => {
    setIsFileGenerating(true);
    try {
      // 模拟接口调用延迟
      await new Promise(resolve => setTimeout(resolve, 1500));
      // 第一阶段：返回固定的模拟模板内容
      const mockTemplate = `基于历史文件学习生成的模板内容：
1、项目背景与目标
2、核心功能模块
3、技术架构设计
4、实施计划
5、风险评估
6、资源配置
7、预期成果`;
      setInfo(mockTemplate);
      message.success('基于文件学习生成成功');
    } catch (error) {
      message.error('生成失败，请重试');
    } finally {
      setIsFileGenerating(false);
    }
  };

  const placeholderTemplate = `样例：
1、 公司概况与历史沿革
2、 商业模式与产品
3、 市场分析
4、 核心壁垒
5、 竞争对比
6、 客户订单进展
7、 未来发展战略
财务与融资`

  return (
    <Modal
      title="添加自定义模板"
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={addLoading}
          onClick={handleSubmit}
        >
          添加模板
        </Button>
      ]}
    >
      <div className="flex gap-6">
        {/* 左侧：自定义模板表单 */}
        <div className="flex-1 space-y-4">

          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              模板标题 <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="请输入模板标题"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
              showCount
            />
          </div>

          
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              模板内容 <span className="text-red-500">*</span>
            </label>
            <TextArea
              placeholder={placeholderTemplate}
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              rows={16}
              maxLength={500}
              showCount
              disabled={isAiGenerating || isFileGenerating}
            />
          </div>



          <div className="mt-8 p-3 bg-blue-50 rounded-md border border-blue-200">
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">💡</span>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">参考建议：</p>
                    <ul className="space-y-1 text-xs">
                      <li>• 模板命名建议简洁明了，如"XXX访谈"、"XXX行业调研"等</li>
                      <li>• 模板内容为待输出纪要的大纲</li>
                      <li>• 创建后的模板将出现在模板选择区域</li>
                    </ul>
                  </div>
                </div>
              </div>
          
          
        </div>

        {/* 右侧：辅助生成模块区域 */}
        <div className="flex-1 mt-6.5 flex flex-col gap-4 max-h-[600px] overflow-y-auto">

          {/* AI生成区域 */}
          <div 
          // className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200"
          >
            {/* <h3 className="text-center text-base font-semibold text-gray-800 mb-4">AI 生成</h3> */}
            
            {/* 基于关键词学习模块 */}
            {/* <div className="bg-white rounded-lg p-3 mb-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">基于关键词学习</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="输入模板要点"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    disabled={isAiGenerating}
                    maxLength={200}
                  />
                  <Button
                    type="primary"
                    onClick={handleAiGenerate}
                    loading={isAiGenerating}
                    disabled={isAiGenerating || isFileGenerating}
                    size="small"
                  >
                    {isAiGenerating ? '生成中' : '生成'}
                  </Button>
                </div>
                <div className="text-xs text-gray-500">
                  💡 输入简要要点，AI 将帮助生成模板大纲
                </div>
              </div>
            </div> */}

            {/* 基于历史文件学习模块 */}
            {/* <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">基于历史文件学习</span>
              </div>
              
              <div className="space-y-2">
                <Button
                  onClick={handleFileGenerate}
                  loading={isFileGenerating}
                  disabled={isAiGenerating || isFileGenerating}
                  className="w-full"
                  size="small"
                >
                  {isFileGenerating ? '学习生成中...' : '基于历史文件生成'}
                </Button>
                <div className="text-xs text-gray-500">
                  💡 建议输入「场景 + 目标」，如：SaaS 客户访谈、竞品调研会议等
                </div>
              </div>
            </div> */}
          </div>

          {/* 案例模板模块 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">案例模板</span>
              <button
                onClick={() => {
                  setTitle(currentTemplate.title);
                  setInfo(currentTemplate.info);
                }}
                className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded border-0 transition-colors"
              >
                复用此模板
              </button>
            </div>
            
            <div className="text-sm font-medium text-gray-800 mb-2">{currentTemplate.title}</div>
            <div className="bg-white p-3 rounded border text-xs text-gray-600 leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto">
              {currentTemplate.info}
            </div>
            
            {/* 切换按钮 */}
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-500">
                {currentTemplateIndex + 1} / {EXAMPLE_TEMPLATES_LIST.length}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevTemplate}
                  className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded border-0 transition-colors"
                >
                  上一个
                </button>
                <button
                  onClick={handleNextTemplate}
                  className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded border-0 transition-colors"
                >
                  下一个
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddTemplateModal;